/* eslint-disable @typescript-eslint/no-explicit-any */
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const { token } = useAuth();

  const { key, cluster, scheme } = config.public.pusher as {
    key: string;
    cluster: string;
    host: string;
    port: string;
    scheme: string;
  };

  let apiBaseURL = config.public?.useProxyPath
    ? '/api'
    : ((config.public?.auth as { baseURL?: string })?.baseURL ?? '');

  // get domain from apiBaseURL
  try {
    const url = new URL(apiBaseURL);
    apiBaseURL = `${url.protocol}//${url.host}/api`;
  } catch {
    console.warn(
      `Invalid API base URL: ${apiBaseURL}. Falling back to original value.`,
    );
  }

  window.Pusher = Pusher;

  const echo = new Echo({
    broadcaster: 'pusher',
    key,
    cluster,
    forceTLS: scheme === 'https',
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        const authToken = token.value;
        const bearerToken = authToken
          ? authToken.startsWith('Bearer ')
            ? authToken
            : `Bearer ${authToken}`
          : null;

        // Trim any trailing slash so the join never produces a double slash
        // (e.g. ".../api/" + "/broadcasting/auth" -> ".../api//broadcasting/auth")
        const authUrl = `${apiBaseURL.replace(/\/+$/, '')}/broadcasting/auth`;

        console.log(
          '[echo] authorizing channel:',
          channel.name,
          'via',
          authUrl,
        );

        $fetch(authUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            ...(bearerToken ? { Authorization: bearerToken } : {}),
          },
          body: {
            socket_id: socketId,
            channel_name: channel.name,
          },
        })
          .then((data) => {
            console.log('[echo] authorized channel:', channel.name);
            callback(null, data as any);
          })
          .catch((error: Error) => {
            console.error(
              '[echo] auth failed for channel:',
              channel.name,
              error,
            );
            callback(error, null);
          });
      },
    }),
  });

  // Track the Pusher connection lifecycle
  const pusher = (echo.connector as any).pusher as Pusher;

  console.log('[echo] initial connection state:', pusher.connection.state);

  pusher.connection.bind(
    'state_change',
    (states: { previous: string; current: string }) => {
      console.log(
        `[echo] connection state: ${states.previous} -> ${states.current}`,
      );
    },
  );

  pusher.connection.bind('connected', () => {
    console.log('[echo] connected, socket id:', pusher.connection.socket_id);
  });

  pusher.connection.bind('disconnected', () => {
    console.warn('[echo] disconnected');
  });

  pusher.connection.bind('error', (err: unknown) => {
    console.error('[echo] connection error:', err);
  });

  return {
    provide: { echo },
  };
});
